import React from 'react';
import {useQuery} from '@tanstack/react-query';
import {Link} from 'react-router-dom';
import Icon from '@/shared/components/Icon';
import Button from '@/shared/components/Button';
import UserService, {type UserCoupon} from '@/shared/services/userService';

interface Props {
    onClose: () => void;
}

/**
 * "Points & Rewards" modal launched from the header points pill. Shows the
 * points balance + active coupons (from Sean's #46 points engine). Replaces
 * the inline PointsWidget card. Chrome mirrors ManageMembershipModal.
 */
const PointsRewardsModal: React.FC<Props> = ({onClose}) => {
    const {data: pointsData} = useQuery({
        queryKey: ['user', 'points'],
        queryFn: () => UserService.getPoints(),
    });
    const {data: couponsData} = useQuery({
        queryKey: ['user', 'coupons'],
        queryFn: () => UserService.getCoupons(),
    });
    const activeCoupons = (couponsData?.coupons ?? []).filter((c: UserCoupon) => c.valid && !c.usedOn);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/30" onClick={onClose} />
            <div className="relative w-full max-w-md rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    <h2 className="text-lg font-semibold text-mld-text-primary">Points &amp; Rewards</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close dialog"
                        className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors">
                        <Icon name="xmark" className="h-5 w-5" />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-mld-primary/10">
                            <Icon name="star" className="h-5 w-5 text-mld-primary" />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500">Points Balance</p>
                            <p className="text-xl font-bold text-mld-text-primary">{pointsData?.totalPoints ?? 0}</p>
                        </div>
                    </div>

                    <div className="border-t border-mld-panel-border" />

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                                <Icon name="tag" className="h-3.5 w-3.5" /> Active Coupons
                            </p>
                            {/* Points to the public store, matching the header's "Shop Tests"
                                nav item (Derrick, 2026-08-13); the internal /shop routes stay
                                live for BioMatch checkout and direct navigation. */}
                            <a
                                href="https://mylabsdirect.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-medium text-mld-primary hover:underline">
                                Shop →
                            </a>
                        </div>
                        {activeCoupons.length === 0 ? (
                            <p className="text-xs text-gray-400">
                                No active coupons.{' '}
                                <Link to="/settings?section=points" className="text-mld-primary hover:underline">
                                    Redeem points in Settings
                                </Link>
                                .
                            </p>
                        ) : (
                            <div className="space-y-2">
                                {activeCoupons.map((coupon: UserCoupon) => (
                                    <div
                                        key={coupon.id}
                                        className="flex items-center justify-between rounded-lg border border-mld-panel-border bg-gray-50 px-3 py-2">
                                        <span className="font-mono text-xs font-semibold text-mld-text-primary">
                                            {coupon.code}
                                        </span>
                                        <span className="text-xs font-medium text-mld-primary">
                                            {coupon.discountType === 'percentage'
                                                ? `${coupon.discountAmount}% off`
                                                : `$${Number(coupon.discountAmount).toFixed(2)} off`}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                    <Link
                        to="/settings?section=points"
                        onClick={onClose}
                        className="text-sm font-medium text-mld-primary hover:underline">
                        Redeem in Settings
                    </Link>
                    <Button variant="secondary" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PointsRewardsModal;
